// app/javascript/election_ballot.js
// Election ballot with cumulative voting, special Abstain handling, enhanced write-in functionality, ballot review, and vote limit enforcement

// =============== Helper functions ===============
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
window.__BallotLogs__ = window.__BallotLogs__ || [];

// Tailwind class presets for ballot UI
const CL = {
  card: "rounded-2xl border border-gray-200 bg-white/90 p-6 shadow-sm backdrop-blur",
  header: "flex flex-col gap-2 mb-2",
  title: "text-lg font-semibold text-gray-900",
  instructions: "text-sm leading-6 text-gray-600",
  meta: "mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500",
  flags: "flex flex-wrap items-center gap-2",
  flag: "inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 font-semibold uppercase tracking-wide text-[0.7rem] text-gray-700 ring-1 ring-gray-200",
  totals: "flex flex-wrap items-center gap-2 font-medium text-gray-500",
  chip: "inline-flex items-center gap-2 rounded-full px-3 py-1 text-[0.7rem] font-medium",
  chipTotal: "bg-gray-100 text-gray-700 ring-1 ring-gray-200",
  chipUsed: "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200",
  chipUnused: "bg-amber-100 text-amber-700 ring-1 ring-amber-200",

  optionsWrap: "mt-5 divide-y divide-gray-100",
  row: "flex flex-col gap-3 py-3 md:flex-row md:items-center md:justify-between rounded-xl transition gap-2",
  rowHover: "hover:bg-gray-50/70",
  label: "flex-1 text-sm font-medium text-gray-900 cursor-pointer leading-5",
  input: "h-5 w-5 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600",
  select: "w-44 min-w-[9rem] pr-8 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-[#491363] focus:ring-[#491363]",

  wiGroup: "mt-5 space-y-3",
  wiGroupRow: "flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3",
  wiLabel: "text-xs font-semibold uppercase tracking-wide text-gray-500 sm:min-w-[72px]",
  wiInput: "flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-[#491363] focus:ring-[#491363]",
  wiAdd: "inline-flex items-center justify-center rounded-lg bg-[#491363] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-[#3a0d4b] focus:outline-none focus:ring-2 focus:ring-[#491363]/40",
  wiDisplay: "hidden items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 shadow-sm",
  wiDisplayText: "flex-1 font-medium",
  wiDelete: "inline-flex items-center justify-center rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/60",

  error: "mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm",

  reviewSections: "space-y-4",
  reviewSection: "rounded-xl border border-gray-200 bg-gray-50 p-4 shadow-sm",
  reviewChoice: "flex items-center justify-between gap-3 rounded-xl border border-gray-100 bg-white px-4 py-2 text-sm text-gray-700 shadow-sm",
  voteCount: "font-semibold text-indigo-600",
};

function assign(el, cls) {
  el.className = cls;
  return el;
}
function debugLog(type, payload = {}) {
  const entry = { type, payload, ts: Date.now() };
  try {
    window.__BallotLogs__.push(entry);
  } catch (e) {
    // ignore logging errors
  }
}
const toInt = (v, d = 0) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : d;
};

function hasWriteInSelection(card) {
  const el = card.querySelector('.wi-hidden-value');
  return !!(el && el.value.trim() !== '');
}

function escapeHTML(s) {
  return String(s).replace(/[&<>"']/g, (c) => (
    c === "&" ? "&amp;" :
    c === "<" ? "&lt;" :
    c === ">" ? "&gt;" :
    c === '"' ? "&quot;" : "&#39;"
  ));
}

function bytesToBase64(bytes) {
  let binary = "";
  const len = bytes.byteLength;
  for (let i = 0; i < len; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function sha256Hex(str) {
  const enc = new TextEncoder();
  const digest = await crypto.subtle.digest("SHA-256", enc.encode(str));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function createElement(tag, className = "", innerHTML = "") {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (innerHTML) el.innerHTML = innerHTML;
  return el;
}

// =============== Schema helpers ===============
function normalizeCats(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v.map(String).map(s => s.trim().toLowerCase()).filter(Boolean);
  if (typeof v === "string") return v.split(/[\s,|]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
  if (typeof v === "object") return Object.keys(v).filter(k => v[k]).map(k => k.toLowerCase());
  return [];
}

function truthy(v) {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") return /^(true|1|yes|y)$/i.test(v.trim());
  return false;
}

function deriveOptions(sec, idx = 0) {
  // Parse voting_categories string into options
  if (typeof sec.voting_categories === "string" && sec.voting_categories.trim()) {
    const parts = sec.voting_categories.split(/[;,\n\r]+/)
      .map(s => s.trim())
      .filter(Boolean);

    return parts.map((label, i) => ({
      id: `o_${i}_${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
      label: label
    }));
  }

  // Fallback options
  return [
    { id: "o_yes", label: "Yes" },
    { id: "o_no", label: "No" }
  ];
}

// Renders the partial-recall explanation card above the ballot sections: the
// same chart the printed Explanation Page carries, so paper and screen say the
// same thing. Wide content scrolls inside its own container rather than pushing
// the page sideways on a phone.
function buildRecallExplanation(explanation) {
  const card = assign(createElement("section"), `${CL.card} bs-card bs-recall-explanation`);

  const heading = assign(
    createElement("div", "", escapeHTML(explanation.title || "How the recall vote is decided")),
    `${CL.title} bs-title`
  );
  card.appendChild(heading);

  (explanation.intro || []).forEach((paragraph) => {
    card.appendChild(assign(
      createElement("div", "", escapeHTML(paragraph)),
      `${CL.instructions} mt-3`
    ));
  });

  const scroller = assign(createElement("div"), "mt-3 overflow-x-auto");
  const table = assign(createElement("table"), "min-w-full text-left text-sm border border-gray-200");

  const columns = explanation.columns || [];
  if (columns.length) {
    const thead = createElement("thead");
    const headRow = createElement("tr");
    columns.forEach((label) => {
      const th = assign(createElement("th", "", escapeHTML(label)),
        "border-b border-gray-200 bg-gray-50 px-3 py-2 font-semibold text-gray-700");
      headRow.appendChild(th);
    });
    thead.appendChild(headRow);
    table.appendChild(thead);
  }

  const tbody = createElement("tbody");
  (explanation.rows || []).forEach((row) => {
    const tr = createElement("tr");
    row.forEach((cell) => {
      const td = assign(createElement("td", "", escapeHTML(cell == null ? "" : String(cell))),
        "border-b border-gray-100 px-3 py-2 align-top text-gray-600");
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
  });
  table.appendChild(tbody);

  scroller.appendChild(table);
  card.appendChild(scroller);

  return card;
}

// =============== Main render function ===============
function renderBallot(schema) {

  const host = document.getElementById("ballot-sections");
  if (!host) return;

  host.innerHTML = "";

  // Partial recall: the same explanation the printed ballot package carries, so
  // an electronic voter reads exactly what a paper voter reads. Appended AFTER
  // the sections, mirroring the packet, where the page follows the ballot.
  const recallExplanation = schema?.recall_explanation || schema?.schema_hash?.recall_explanation;

  const sections = schema?.sections || schema?.schema_hash?.sections || [];

  sections.forEach((sec, idx) => {

    // Create section card
    const card = assign(createElement("fieldset"), `${CL.card} bs-card`);
    card.dataset.sectionKey = sec.key || `section_${sec.id ?? idx}`;
    card.setAttribute("role", "group");

    // Parse section properties
    const cats = normalizeCats(sec.voting_categories);
    let totalVotes = toInt(sec.max_select, 1);
    if (totalVotes < 1) totalVotes = 1;

    // FIXED: Only trust server boolean values, ignore categories for cumulative detection
    const isCumulative = truthy(sec.is_cumulative);
    const isMultiple = truthy(sec.is_multiple_vote) || totalVotes > 1;


    // Create header
    const header = assign(createElement("legend"), `${CL.header} bs-header`);
    const title = assign(createElement("div", "", escapeHTML(sec.title || `Section ${idx + 1}`)), `${CL.title} bs-title`);
    header.appendChild(title);

    if (sec.instructions) {
      // instructions_html is built server-side by Ballots::Linkify: the author's
      // text escaped, with anchors added for the addresses they typed. It is
      // used as markup only because the server is the one that produced the
      // markup — sec.instructions, which is raw author text, is still escaped.
      const instructions = assign(createElement("div"), `${CL.instructions} bs-instructions`);
      if (sec.instructions_html) {
        instructions.innerHTML = sec.instructions_html;
      } else {
        instructions.textContent = sec.instructions;
      }
      header.appendChild(instructions);
    }
    card.appendChild(header);

    // Create meta info
    const meta = assign(createElement("div"), `${CL.meta} bs-meta`);
    const flagText = isCumulative ?
      "Cumulative" :
      (isMultiple ? `Pick up to ${totalVotes}` : "Pick 1");

    meta.innerHTML = `
      <div class="${CL.flags} bs-flags">
        <span class="${CL.flag} bs-flag">${flagText}</span>
      </div>
      <div class="${CL.totals} bs-totals" aria-live="polite"></div>
    `;
    card.appendChild(meta);

    // Create options
    const optionsContainer = assign(createElement("div"), `${CL.optionsWrap} bs-options`);
    const options = deriveOptions(sec, idx);


    // Separate Abstain from regular options
    const abstainOption = options.find(opt => /abstain/i.test(opt.label));
    const regularOptions = options.filter(opt => !/abstain/i.test(opt.label));

    if (abstainOption) {
    }

    if (isCumulative) {

      // Cumulative voting: create select dropdowns ONLY for regular options
      regularOptions.forEach((opt, oi) => {
        const optionLabel = String(opt.label ?? opt.name ?? opt.value ?? opt.id ?? "").trim();
        const row = createElement("div");
        row.className = "bs-row flex items-center gap-3 py-3 rounded-xl";
        row.dataset.optionId = opt.id;
        row.dataset.optionLabel = optionLabel;

        const selectId = `s_${idx}_o_${oi}_sel`;
        row.innerHTML = `
          <label for="${selectId}" class="option-label text-sm font-medium text-gray-900 text-center sm:text-left leading-5 select-none sm:ml-2 min-w-0 flex-1 pr-4">
            ${escapeHTML(optionLabel)}
          </label>
          <select id="${selectId}" class="cum-select shrink-0 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm
                                      pr-10 focus:border-[#491363] focus:ring-[#491363]
                                     "
                  style="min-width: 7rem; max-width: 9rem; width: 7rem;">
            <option value="">0</option>
          </select>
        `;

        enhanceRowInteractivity(row);
        optionsContainer.appendChild(row);
      });

      // Add Abstain as checkbox AFTER regular options
      if (abstainOption) {

        const row = createElement("div");
        row.className = "bs-row flex items-center gap-3 py-3 rounded-xl abstain-row";
        row.dataset.optionId = abstainOption.id;
        row.dataset.optionLabel = String(abstainOption.label ?? abstainOption.name ?? abstainOption.value ?? abstainOption.id ?? "").trim();

        const checkboxId = `s_${idx}_abstain_cb`;
        const checkbox = createElement("input");
        checkbox.type = "checkbox";
        checkbox.id = checkboxId;
        checkbox.className = "abstain-checkbox h-5 w-5 mr-2 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600";


        const label = createElement("label");
        label.className = "option-label text-sm font-medium text-gray-900 leading-5 select-none";
        label.setAttribute("for", checkboxId);
        label.textContent = abstainOption.label;

        const hiddenInput = createElement("input");
        hiddenInput.type = "hidden";
        hiddenInput.className = "abstain-hidden";
        hiddenInput.value = "";

        row.appendChild(checkbox);
        row.appendChild(label);
        row.appendChild(hiddenInput);
        enhanceRowInteractivity(row);
        optionsContainer.appendChild(row);

      }
    } else {

      // Regular voting: create radio buttons or checkboxes for regular options
      const inputType = isMultiple ? "checkbox" : "radio";
      const inputName = (sec.key || `section_${idx}`) + (isMultiple ? "[]" : "");


      // Regular options
      regularOptions.forEach((opt, oi) => {
        const optionLabel = String(opt.label ?? opt.name ?? opt.value ?? opt.id ?? "").trim();

        const inputId = `s_${idx}_o_${oi}_${inputType}`;
        const row = createElement("div");
        row.className = "bs-row flex items-center gap-3 py-3 rounded-xl";
        row.dataset.optionId = opt.id;
        row.dataset.optionLabel = optionLabel;

        const input = createElement("input");
        input.type = inputType;
        input.id = inputId;
        input.name = inputName;
        input.className = "regular-input h-5 w-5 mr-2 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600";

        const label = createElement("label");
        label.className = "option-label text-sm font-medium text-gray-900 leading-5 select-none";
        label.setAttribute("for", inputId);
        label.textContent = optionLabel;

        row.appendChild(input);
        row.appendChild(label);
        enhanceRowInteractivity(row);
        optionsContainer.appendChild(row);
      });

      // Add Abstain with appropriate input type
      if (abstainOption) {
        const abstainInputType = isMultiple ? "checkbox" : "radio";

        const row = createElement("div");
        row.className = "bs-row flex items-center gap-3 py-3 rounded-xl abstain-row";
        row.dataset.optionId = abstainOption.id;
        row.dataset.optionLabel = String(abstainOption.label ?? abstainOption.name ?? abstainOption.value ?? abstainOption.id ?? "").trim();

        const abstainId = `s_${idx}_abstain_input`;
        const abstainInput = createElement("input");
        abstainInput.type = abstainInputType;
        abstainInput.id = abstainId;
        abstainInput.className = `${isMultiple ? "abstain-checkbox" : "abstain-radio regular-input"} h-5 w-5 mr-2 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600`;


        // For radio sections, use the same name as other inputs
        if (!isMultiple) {
          abstainInput.name = inputName;
        }

        const label = createElement("label");
        label.className = "option-label text-sm font-medium text-gray-900 leading-5 select-none";
        label.setAttribute("for", abstainId);
        label.textContent = abstainOption.label;

        const hiddenInput = createElement("input");
        hiddenInput.type = "hidden";
        hiddenInput.className = "abstain-hidden";
        hiddenInput.value = "";

        row.appendChild(abstainInput);
        row.appendChild(label);
        row.appendChild(hiddenInput);
        enhanceRowInteractivity(row);
        optionsContainer.appendChild(row);

      }
    }

    card.appendChild(optionsContainer);

    // Add enhanced write-in if needed
    if (sec.write_in) {
      const writeInContainer = assign(createElement("div"), `wi-group ${CL.wiGroup}`);
      writeInContainer.innerHTML = `
        <div class="wi-input-group flex items-center gap-3 flex-wrap">
          <label class="wi-label text-xs font-semibold uppercase tracking-wide text-gray-500" for="wi_${idx}">Write-in</label>
          <input id="wi_${idx}"
                 class="wi-input flex-1 min-w-[14rem] mx-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 shadow-sm focus:border-[#491363] focus:ring-[#491363] ml-2 mr-2"
                 type="text"
                 placeholder="Enter a write-in name">
          <button type="button" class="wi-add-btn inline-flex items-center justify-center rounded-lg bg-[#491363] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-[#3a0d4b] focus:outline-none focus:ring-2 focus:ring-[#491363]/40">Add</button>
        </div>
        <div class="wi-display hidden items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 shadow-sm">
          <span class="wi-display-text flex-1 font-medium"></span>
          <button type="button" class="wi-delete-btn inline-flex items-center justify-center rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/60">Remove</button>
          <input type="hidden" class="wi-hidden-value" value="">
        </div>
      `;
      card.appendChild(writeInContainer);
    }

    // Add error container
    const errorContainer = assign(createElement("div"), `${CL.error} bs-error`);
    errorContainer.id = `bs-error-${idx}`;
    errorContainer.style.display = "none";
    errorContainer.setAttribute("role", "alert");
    card.appendChild(errorContainer);

    // Add card to DOM
    host.appendChild(card);


    // Setup controls AFTER adding to DOM
    setupSectionControls(card, {
      isCumulative,
      isMultiple,
      totalVotes,
      sectionIndex: idx
    });
  });

  // The explanation follows the ballot, as it does in the printed packet.
  if (recallExplanation) {
    host.appendChild(buildRecallExplanation(recallExplanation));
  }
}

function enhanceRowInteractivity(row) {
  row.setAttribute('tabindex', '0');
  row.classList.add('cursor-pointer');

  const input = row.querySelector('input, select');
  const label = row.querySelector('label');

  // Click anywhere on the row toggles the control
  row.addEventListener('click', (e) => {
    // avoid double-toggle when clicking on actual control
    if (e.target === input) return;
    if (input?.tagName === 'SELECT') {
      input.focus();
    } else if (input?.type === 'checkbox') {
      input.checked = !input.checked;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (input?.type === 'radio') {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  // Space/Enter should act like click
  row.addEventListener('keydown', (e) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      row.click();
    }
  });

  // Maintain label association for a11y
  if (input && label && !label.getAttribute('for') && input.id) {
    label.setAttribute('for', input.id);
  }
}


// =============== Write-in controls setup ===============
function setupWriteInControls(card, { isCumulative, isMultiple, totalVotes, sectionIndex }) {
  const writeInContainer = card.querySelector('.wi-group');
  if (!writeInContainer) return;

  const inputGroup = writeInContainer.querySelector('.wi-input-group');
  const displayGroup = writeInContainer.querySelector('.wi-display');
  const input = writeInContainer.querySelector('.wi-input');
  const addBtn = writeInContainer.querySelector('.wi-add-btn');
  const deleteBtn = writeInContainer.querySelector('.wi-delete-btn');
  const displayText = writeInContainer.querySelector('.wi-display-text');
  const hiddenValue = writeInContainer.querySelector('.wi-hidden-value');

  if (!input || !addBtn || !deleteBtn || !displayText || !hiddenValue) return;


  // Function to add write-in candidate
  function addWriteIn() {
    const name = input.value.trim();
    if (!name) {
      showError(card, "Please enter a name for the write-in candidate.");
      return;
    }

    // Clear abstain if it's checked
    const abstainInput = card.querySelector('.abstain-checkbox, .abstain-radio');
    const abstainHidden = card.querySelector('.abstain-hidden');
    if (abstainInput && abstainInput.checked) {
      abstainInput.checked = false;
      if (abstainHidden) {
        abstainHidden.value = "";
      }
    }

    // Set the write-in display
    displayText.textContent = `${name} (Write-In Candidate)`;
    hiddenValue.value = name;

    // Switch display
    inputGroup.style.display = 'none';
    displayGroup.style.display = 'flex';


    // Update vote totals
    if (card._updateVoteTotals) {
      card._updateVoteTotals();
    }

    // Update ballot review
    updateBallotReview();
  }

  // Function to delete write-in candidate
  function deleteWriteIn() {
    const oldName = hiddenValue.value;
    input.value = '';
    hiddenValue.value = '';
    displayText.textContent = '';

    // Switch display
    inputGroup.style.display = 'flex';
    displayGroup.style.display = 'none';


    // Update vote totals
    if (card._updateVoteTotals) {
      card._updateVoteTotals();
    }

    // Update ballot review
    updateBallotReview();
  }

  // Event listeners
  addBtn.addEventListener('click', addWriteIn);
  deleteBtn.addEventListener('click', deleteWriteIn);

  // Allow Enter key to add write-in
  input.addEventListener('keypress', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addWriteIn();
    }
  });

  // Store reference for other functions to call
  card._deleteWriteIn = deleteWriteIn;
}

// =============== Section controls setup ===============
function setupSectionControls(card, { isCumulative, isMultiple, totalVotes, sectionIndex }) {

  const totalsEl = card.querySelector(".bs-totals");

  if (isCumulative) {
    setupCumulativeControls(card, totalVotes, totalsEl);
  } else {
    setupRegularControls(card, isMultiple, totalVotes, totalsEl);
  }

  // Setup write-in controls
  setupWriteInControls(card, { isCumulative, isMultiple, totalVotes, sectionIndex });
}

function setupCumulativeControls(card, totalVotes, totalsEl) {
  const selects = Array.from(card.querySelectorAll(".cum-select"));
  const abstainCheckbox = card.querySelector(".abstain-checkbox");
  const abstainHidden = card.querySelector(".abstain-hidden");
  const writeInContainer = card.querySelector(".wi-group");


  if (selects.length === 0 && !abstainCheckbox) return;

  // Initialize all selects to empty
  selects.forEach(select => {
    select.value = "";
  });

  // Function to clear all regular selections
  function clearRegularSelections() {
    selects.forEach(select => {
      select.value = "";
    });
  }

  // Function to clear write-in
  function clearWriteIn() {
    if (card._deleteWriteIn) {
      const hiddenValue = card.querySelector('.wi-hidden-value');
      if (hiddenValue && hiddenValue.value.trim() !== '') {
        card._deleteWriteIn();
      }
    }
  }

  // Function to update write-in availability
  function updateWriteInAvailability(remainingVotes) {
    if (!writeInContainer) return;

    const inputGroup = writeInContainer.querySelector('.wi-input-group');
    const displayGroup = writeInContainer.querySelector('.wi-display');
    const hasWriteIn = hasWriteInSelection(card);

    if (remainingVotes <= 0 && !hasWriteIn) {
      // Hide write-in input if no votes remaining and no write-in exists
      inputGroup.style.display = 'none';
    } else if (!hasWriteIn) {
      // Show write-in input if votes available and no write-in exists
      inputGroup.style.display = 'flex';
      displayGroup.style.display = 'none';
    }
    // If write-in exists, display group should already be shown by write-in controls
  }

  // Function to update select options based on remaining votes
  function updateSelectOptions() {
    const hasWriteIn = hasWriteInSelection(card);
    const writeInVotes = hasWriteIn ? 1 : 0;

    if (abstainCheckbox && abstainCheckbox.checked) {
      // If abstain is checked, clear write-in and regular selections
      clearWriteIn();
      clearRegularSelections();

      if (abstainHidden) {
        abstainHidden.value = totalVotes.toString();
      }
      updateTotalsDisplay(totalsEl, totalVotes, totalVotes);

      // Reset all selects when abstain is checked but keep them interactive
      selects.forEach(select => {
        const maxForThisSelect = totalVotes;
        select.innerHTML = '<option value="">0</option>';
        for (let i = 1; i <= maxForThisSelect; i++) {
          const option = document.createElement("option");
          option.value = i.toString();
          option.textContent = i.toString();
          select.appendChild(option);
        }
        select.disabled = false;
      });

      // Hide write-in when abstaining
      if (writeInContainer) {
        writeInContainer.querySelector('.wi-input-group').style.display = 'none';
      }

      // Update ballot review
      updateBallotReview();
      return;
    } else {
      // Enable selects and clear abstain hidden value
      selects.forEach(select => {
        select.disabled = false;
      });
      if (abstainHidden) {
        abstainHidden.value = "";
      }
    }

    const usedVotes = selects.reduce((sum, select) => {
      return sum + toInt(select.value, 0);
    }, 0);

    const remainingVotes = Math.max(0, totalVotes - usedVotes - writeInVotes);

    // Update write-in availability based on remaining votes
    updateWriteInAvailability(remainingVotes);

    // Update each select's options
    selects.forEach(select => {
      const currentValue = select.value;
      const currentVotes = toInt(currentValue, 0);
      const maxForThisSelect = remainingVotes + currentVotes;

      // Rebuild options
      select.innerHTML = '<option value="">0</option>';
      for (let i = 1; i <= maxForThisSelect; i++) {
        const option = document.createElement("option");
        option.value = i.toString();
        option.textContent = i.toString();
        select.appendChild(option);
      }

      // Restore current value if still valid
      if (currentVotes <= maxForThisSelect) {
        select.value = currentValue;
      } else {
        select.value = "";
      }
    });

    // Update totals display
    updateTotalsDisplay(totalsEl, usedVotes + writeInVotes, totalVotes);

    // Update ballot review
    updateBallotReview();
  }

  // Add event listeners for selects
  selects.forEach(select => {
    select.addEventListener("change", () => {
      // If user changes any dropdown value, uncheck abstain
      if (abstainCheckbox) {
        abstainCheckbox.checked = false;
      }
      updateSelectOptions();
    });
    select.addEventListener("focus", updateSelectOptions);
  });

  // Add event listener for abstain checkbox
  if (abstainCheckbox) {
    abstainCheckbox.addEventListener("change", () => {
      if (abstainCheckbox.checked) {
        clearRegularSelections();
      }
      updateSelectOptions();
    });
  }

  // Store update function for write-in to use
  card._updateVoteTotals = updateSelectOptions;

  // Initial setup
  updateSelectOptions();
}

function setupRegularControls(card, isMultiple, totalVotes, totalsEl) {
  const regularInputs = Array.from(card.querySelectorAll('.regular-input'));
  const abstainInput = card.querySelector(".abstain-checkbox, .abstain-radio");
  const abstainHidden = card.querySelector(".abstain-hidden");
  const writeInContainer = card.querySelector(".wi-group");


  // Function to clear all regular selections (excluding abstain)
  function clearRegularSelections() {
    regularInputs.forEach(input => {
      if (!input.classList.contains('abstain-radio') && !input.classList.contains('abstain-checkbox')) {
        input.checked = false;
      }
    });
  }

  // Function to clear write-in
  function clearWriteIn() {
    if (card._deleteWriteIn) {
      const hiddenValue = card.querySelector('.wi-hidden-value');
      if (hiddenValue && hiddenValue.value.trim() !== '') {
        card._deleteWriteIn();
      }
    }
  }

  // Function to update write-in availability
  function updateWriteInAvailability(usedVotes, hasWriteIn) {
    if (!writeInContainer) return;

    const inputGroup = writeInContainer.querySelector('.wi-input-group');
    const displayGroup = writeInContainer.querySelector('.wi-display');

    if (isMultiple && totalVotes > 0) {
      const votesWithoutWriteIn = usedVotes - (hasWriteIn ? 1 : 0);
      const remainingVotes = totalVotes - votesWithoutWriteIn;

      if (remainingVotes <= 0 && !hasWriteIn) {
        // Hide write-in input if no votes remaining and no write-in exists
        inputGroup.style.display = 'none';
      } else if (!hasWriteIn) {
        // Show write-in input if votes available and no write-in exists
        inputGroup.style.display = 'flex';
        displayGroup.style.display = 'none';
      }
      // If write-in exists, display group should already be shown by write-in controls
    } else if (!isMultiple) {
      // For single-vote sections, hide write-in if any other option is selected
      if (usedVotes > (hasWriteIn ? 1 : 0) && !hasWriteIn) {
        inputGroup.style.display = 'none';
      } else if (!hasWriteIn) {
        inputGroup.style.display = 'flex';
        displayGroup.style.display = 'none';
      }
    }
  }

  function updateTotals() {
    const hasWriteIn = hasWriteInSelection(card);
    const writeInVotes = hasWriteIn ? 1 : 0;

    if (abstainInput && abstainInput.checked) {

      // Clear write-in and regular selections if abstain is checked
      clearWriteIn();
      clearRegularSelections();

      if (abstainHidden) {
        const valueToSet = isMultiple ? totalVotes : 1;
        abstainHidden.value = valueToSet.toString();
      }

      // Hide write-in when abstaining
      if (writeInContainer) {
        writeInContainer.querySelector('.wi-input-group').style.display = 'none';
      }

      const displayVotes = isMultiple ? totalVotes : 1;
      updateTotalsDisplay(totalsEl, displayVotes, isMultiple ? totalVotes : 1);

      // Update ballot review
      updateBallotReview();
      return;
    }

    // Clear abstain hidden value if abstain is not checked
    if (abstainHidden) {
      abstainHidden.value = "";
    }

    // Count all checked inputs except abstain
    const allInputs = Array.from(card.querySelectorAll('input[type="checkbox"], input[type="radio"]'));
    const checkedCount = allInputs.filter(input =>
      input.checked &&
      !input.classList.contains('abstain-checkbox') &&
      !input.classList.contains('abstain-radio')
    ).length;

    const totalUsed = checkedCount + writeInVotes;
    debugLog('regular-count', {
      cardKey: card.dataset.sectionKey,
      checkedCount,
      writeInVotes,
      totalUsed,
      totalVotes,
      isMultiple
    });

    // Update write-in availability based on vote usage
    updateWriteInAvailability(totalUsed, hasWriteIn);

    updateTotalsDisplay(totalsEl, totalUsed, isMultiple ? totalVotes : 1);

    // Update ballot review
    updateBallotReview();
  }

  // Add event listeners for all inputs in the section
  const allInputs = Array.from(card.querySelectorAll('input[type="checkbox"], input[type="radio"]'));
  allInputs.forEach(input => {
    input.addEventListener("change", () => {
      const hasWriteIn = hasWriteInSelection(card);

      // If user selects a non-abstain option, clear abstain
      if (abstainInput && input !== abstainInput && input.checked) {
        abstainInput.checked = false;
        if (abstainHidden) {
          abstainHidden.value = "";
        }
      }

      if (isMultiple && totalVotes > 0 && !input.classList.contains('abstain-checkbox') && !input.classList.contains('abstain-radio')) {
        const nonAbstainChecked = allInputs.filter(i =>
          i.checked &&
          !i.classList.contains('abstain-checkbox') &&
          !i.classList.contains('abstain-radio')
        );

        const writeInVotes = hasWriteIn ? 1 : 0;
        const totalSelected = nonAbstainChecked.length + writeInVotes;

        debugLog('regular-change', {
          cardKey: card.dataset.sectionKey,
          inputId: input.id,
          checked: input.checked,
          totalSelected,
          totalVotes,
          writeInVotes,
          nonAbstainChecked: nonAbstainChecked.map(el => el.id)
        });

        if (totalSelected > totalVotes) {
          input.checked = false;
          debugLog('regular-cap-hit', {
            cardKey: card.dataset.sectionKey,
            inputId: input.id,
            totalSelected,
            totalVotes,
            writeInVotes,
            nonAbstainChecked: nonAbstainChecked.map(el => el.id)
          });
          showError(card, `You may select up to ${totalVotes} option${totalVotes === 1 ? "" : "s"}.`);
        }
      }
      updateTotals();
    });
  });

  // Store update function for write-in to use
  card._updateVoteTotals = updateTotals;

  updateTotals();
}

function updateTotalsDisplay(totalsEl, used, max) {
  if (!totalsEl) return;

  const unused = Math.max(0, max - used);
  const chipBase = `${CL.chip} bs-chip`;
  totalsEl.innerHTML = `
    <span class="${chipBase} ${CL.chipTotal} bs-chip-total">Votes permitted: <strong>${max}</strong></span>
    <span class="${chipBase} ${CL.chipUsed} bs-chip-used">Used: <strong>${used}</strong></span>
    <span class="${chipBase} ${CL.chipUnused} bs-chip-unused">Unused: <strong>${unused}</strong></span>
  `;
}

function showError(card, message) {
  const errorEl = card.querySelector(".bs-error");
  if (errorEl) {
    errorEl.textContent = message;
    errorEl.style.display = "block";
    setTimeout(() => {
      errorEl.style.display = "none";
    }, 3000);
  }
}

// =============== Ballot Review ===============
function generateBallotReview(schema) {

  const sections = [];
  const host = document.getElementById("ballot-sections");
  const cards = Array.from(host.querySelectorAll(".bs-card"));

  cards.forEach((card, cardIndex) => {
    const key = card.dataset.sectionKey;
    const isCumulative = card.querySelector(".cum-select") !== null;

    // Get section title from schema
    const schemaSections = schema?.sections || schema?.schema_hash?.sections || [];
    const sectionSchema = schemaSections[cardIndex];
    const sectionTitle = sectionSchema?.title || `Section ${cardIndex + 1}`;

    // Get write-in value
    const writeInHidden = card.querySelector(".wi-hidden-value");
    const writeIn = writeInHidden ? writeInHidden.value.trim() : null;

    const reviewSection = {
      title: sectionTitle,
      key: key,
      choices: [],
      isCumulative: isCumulative
    };

    if (isCumulative) {
      // Collect cumulative votes for review
      const rows = Array.from(card.querySelectorAll(".bs-row[data-option-id]"));

      rows.forEach(row => {
        const optionId = row.dataset.optionId;

        if (row.classList.contains('abstain-row')) {
          // Handle abstain row
          const checkbox = row.querySelector(".abstain-checkbox");
          const hiddenInput = row.querySelector(".abstain-hidden");
          const label = row.querySelector("label").textContent.trim();

          if (checkbox && checkbox.checked && hiddenInput && hiddenInput.value) {
            reviewSection.choices.push({
              label: label,
              votes: null,
              isAbstain: true
            });
          }
        } else {
          // Handle regular cumulative selection
          const select = row.querySelector(".cum-select");
          const label = row.querySelector("label").textContent.trim();
          const votes = toInt(select?.value, 0);

          if (votes > 0) {
            reviewSection.choices.push({
              label: label,
              votes: votes,
              isAbstain: false
            });
          }
        }
      });

      // Add write-in with 1 vote if present
      if (writeIn) {
        reviewSection.choices.push({
          label: `${writeIn} (Write-In Candidate)`,
          votes: null,
          isAbstain: false
        });
      }
    } else {
      // Collect regular votes for review
      const rows = Array.from(card.querySelectorAll(".bs-row[data-option-id]"));

      rows.forEach(row => {
        const optionId = row.dataset.optionId;

        if (row.classList.contains('abstain-row')) {
          // Handle abstain row
          const abstainInput = row.querySelector(".abstain-checkbox, .abstain-radio");
          const label = row.querySelector("label").textContent.trim();

          if (abstainInput && abstainInput.checked) {
            reviewSection.choices.push({
              label: label,
              votes: null, // Regular voting doesn't show vote counts
              isAbstain: true
            });
          }
        } else {
          // Handle regular input (excluding abstain inputs that also have regular-input class)
          const input = row.querySelector('.regular-input');
          const label = row.querySelector("label").textContent.trim();

          if (input && input.checked && !input.classList.contains('abstain-radio')) {
            reviewSection.choices.push({
              label: label,
              votes: null, // Regular voting doesn't show vote counts
              isAbstain: false
            });
          }
        }
      });

      // Add write-in if present
      if (writeIn) {
        reviewSection.choices.push({
          label: `${writeIn} (Write-In Candidate)`,
          votes: null, // Regular voting doesn't show vote counts
          isAbstain: false
        });
      }
    }

    if (reviewSection.choices.length > 0) {
      sections.push(reviewSection);
    }
  });

  return sections;
}

function renderBallotReview(reviewData) {
  const reviewContainer = document.getElementById("ballot-review");
  if (!reviewContainer) return;

  if (reviewData.length === 0) {
    reviewContainer.innerHTML = `
      <div class="review-empty">
        <p class="text-gray-500 text-center py-8">Make your selections above to see them reviewed here.</p>
      </div>
    `;
    return;
  }

  let reviewHTML = `<div class="review-sections ${CL.reviewSections}">`;

  reviewData.forEach((section, index) => {
    reviewHTML += `
      <div class="${CL.reviewSection} review-section">
        <h3 class="text-lg font-semibold text-gray-900 mb-3 review-section-title">
          ${escapeHTML(section.title)}
        </h3>
        <div class="space-y-2">
    `;

    section.choices.forEach(choice => {
      const voteDisplay = section.isCumulative && choice.votes !== null ?
        ` <span class="${CL.voteCount} vote-count">(${choice.votes} vote${choice.votes === 1 ? '' : 's'})</span>` : '';

      const choiceClass = choice.isAbstain ?
        'text-yellow-700' :
        'text-gray-800';

      reviewHTML += `
        <div class="${CL.reviewChoice} review-choice">
          <span class="choice-label ${choiceClass}">
            ${escapeHTML(choice.label)}
          </span>
          ${voteDisplay}
        </div>
      `;
    });

    reviewHTML += `
        </div>
      </div>
    `;
  });

  reviewHTML += '</div>';
  reviewContainer.innerHTML = reviewHTML;
}

function updateBallotReview() {

  const reviewContainer = document.getElementById("ballot-review");
  if (!reviewContainer) {
    console.error("ballot-review container not found in DOM");
    return;
  }

  const schemaJSON = document.getElementById("ballot-schema")?.textContent;
  if (!schemaJSON) {
    console.error("ballot-schema script tag not found");
    return;
  }

  try {
    const schema = JSON.parse(schemaJSON);

    const reviewData = generateBallotReview(schema);

    renderBallotReview(reviewData);

    // Show/hide confirm checkbox and submit button based on whether there are selections
    const confirmContainer = document.querySelector('.confirm-container');
    const submitContainer = document.querySelector('.submit-container');

    if (reviewData.length > 0) {
      confirmContainer?.classList.remove('hidden');
      submitContainer?.classList.remove('hidden');
    } else {
      confirmContainer?.classList.add('hidden');
      submitContainer?.classList.add('hidden');
    }
  } catch (e) {
    console.error("Failed to update ballot review:", e);
  }
}

// =============== Data collection ===============
function collectFromSections(schema, options = {}) {
  const { validate = true } = options;
  const sections = [];
  const host = document.getElementById("ballot-sections");
  const cards = Array.from(host.querySelectorAll(".bs-card"));

  cards.forEach(card => {
    const key = card.dataset.sectionKey;
    const isCumulative = card.querySelector(".cum-select") !== null;

    // Clear any previous errors
    const errorEl = card.querySelector(".bs-error");
    if (errorEl) {
      errorEl.style.display = "none";
      errorEl.textContent = "";
    }

    // Get write-in value from hidden input
    const writeInHidden = card.querySelector(".wi-hidden-value");
    const writeIn = writeInHidden ? writeInHidden.value.trim() : null;

    if (isCumulative) {
      // Collect cumulative votes
      const points = {};
      const rows = Array.from(card.querySelectorAll(".bs-row[data-option-id]"));

      rows.forEach(row => {
        const optionId = row.dataset.optionId;
        const optionLabel = (row.dataset.optionLabel || row.querySelector("label")?.textContent || "").trim();
        const optionKey = optionLabel || optionId;

        if (row.classList.contains('abstain-row')) {
          // Handle abstain row
          const checkbox = row.querySelector(".abstain-checkbox");
          const hiddenInput = row.querySelector(".abstain-hidden");

          if (checkbox && checkbox.checked && hiddenInput && hiddenInput.value) {
            if (optionKey) {
              points[optionKey] = toInt(hiddenInput.value, 0);
            }
          }
        } else {
          // Handle regular cumulative selection
          const select = row.querySelector(".cum-select");
          const votes = toInt(select?.value, 0);
          if (votes > 0) {
            if (optionKey) {
              points[optionKey] = votes;
            }
          }
        }
      });

      // Add write-in with 1 vote if present
      if (writeIn) {
        points["write_in"] = 1;
      }

      if (Object.keys(points).length === 0) {
        if (validate) {
          showError(card, "Please allocate at least 1 vote or provide a write-in.");
          throw new Error("Validation failed");
        }
        return;
      }

      const sectionData = { key, points };
      if (writeIn) {
        sectionData.write_in = `${writeIn} (Write-In Candidate)`;
      }
      sections.push(sectionData);
    } else {
      // Collect regular votes
      const selected = [];
      const rows = Array.from(card.querySelectorAll(".bs-row[data-option-id]"));

      rows.forEach(row => {
        const optionId = row.dataset.optionId;
        const optionLabel = (row.dataset.optionLabel || row.querySelector("label")?.textContent || "").trim();
        const optionKey = optionLabel || optionId;

        if (row.classList.contains('abstain-row')) {
          // Handle abstain row
          const abstainInput = row.querySelector(".abstain-checkbox, .abstain-radio");
          if (abstainInput && abstainInput.checked) {
            if (optionKey) {
              selected.push(optionKey);
            }
          }
        } else {
          // Handle regular input (excluding abstain inputs that also have regular-input class)
          const input = row.querySelector('.regular-input');
          if (input && input.checked && !input.classList.contains('abstain-radio')) {
            if (optionKey) {
              selected.push(optionKey);
            }
          }
        }
      });

      // Add write-in if present
      if (writeIn) {
        selected.push("write_in");
      }

      if (selected.length === 0) {
        if (validate) {
          showError(card, "Please make a selection or provide a write-in.");
          throw new Error("Validation failed");
        }
        return;
      }

      const sectionData = { key, selected };
      if (writeIn) {
        sectionData.write_in = `${writeIn} (Write-In Candidate)`;
      }
      sections.push(sectionData);
    }
  });

  return sections;
}

// =============== Encryption functions ===============
async function getRsaPubKey() {
  // Try script tag first, then meta tag as fallback
  let pem = $("#rsa-public-key")?.textContent?.trim();

  if (!pem) {
    const metaTag = document.querySelector('meta[name="vote-public-key-pem"]');
    pem = metaTag?.getAttribute('content')?.trim();
  }

  if (!pem) throw new Error("Missing public key");
  const b64 = pem.replace(/-----(BEGIN|END) PUBLIC KEY-----/g, "").replace(/\s+/g, "");
  const buf = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)).buffer;
  return await crypto.subtle.importKey("spki", buf, { name: "RSA-OAEP", hash: "SHA-256" }, false, ["encrypt"]);
}

async function aesGcmEncrypt(plaintext, key, iv, additionalData) {
  const enc = new TextEncoder();
  const params = { name: "AES-GCM", iv };
  if (additionalData) {
    params.additionalData = additionalData;
  }
  const ct = await crypto.subtle.encrypt(params, key, enc.encode(plaintext));
  return new Uint8Array(ct);
}

async function genAesKey() {
  return await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt"]);
}

async function exportRaw(key) {
  const raw = await crypto.subtle.exportKey("raw", key);
  return new Uint8Array(raw);
}

async function rsaWrapRaw(rawKeyBytes, rsaPubKey) {
  return new Uint8Array(await crypto.subtle.encrypt({ name: "RSA-OAEP" }, rsaPubKey, rawKeyBytes));
}

function b64u(bytes) {
  const str = btoa(String.fromCharCode(...bytes));
  return str.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function buildEncryptedPayload({ sections, schemaHash, orderId }) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const aesKey = await genAesKey();
  const rsaPub = await getRsaPubKey();
  const requestId = (crypto.randomUUID ? crypto.randomUUID() : `req-${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const payloadBody = JSON.stringify({ schema_hash: schemaHash || null, sections });

  const aadString = orderId ? `${orderId}|${requestId}` : requestId;
  const aadBytes = new TextEncoder().encode(aadString);

  const ctWithTag = await aesGcmEncrypt(payloadBody, aesKey, iv, aadBytes);
  const tagLength = 16; // bytes
  if (ctWithTag.length <= tagLength) {
    throw new Error("Ciphertext too short");
  }

  const cipherBytes = ctWithTag.slice(0, ctWithTag.length - tagLength);
  const tagBytes = ctWithTag.slice(ctWithTag.length - tagLength);

  const raw = await exportRaw(aesKey);
  const wrapped = await rsaWrapRaw(raw, rsaPub);

  return {
    ct_b64: bytesToBase64(cipherBytes),
    iv_b64: bytesToBase64(iv),
    key_wrapped_b64: bytesToBase64(wrapped),
    tag_b64: bytesToBase64(tagBytes),
    ballot_hash_hex: await sha256Hex(payloadBody),
    request_id: requestId,
    aad: aadString,
  };
}

// =============== Form submission ===============
function runEncryptionPipeline(form) {
  const errors = document.getElementById("ballot-errors");
  if (errors) {
    errors.classList.add("hidden");
    errors.textContent = "";
  }

  (async () => {
    try {
      const confirmBox = document.getElementById("confirm-checkbox");
      if (confirmBox && !confirmBox.checked) {
        throw new Error("Please confirm your ballot before submitting.");
      }

      const schemaJSON = document.getElementById("ballot-schema")?.textContent;
      const schema = JSON.parse(schemaJSON || "{}");

      debugLog('submit-start', {
        confirmChecked: confirmBox ? confirmBox.checked : null,
        hasSchema: !!schema,
        orderId: form?.dataset?.orderId || null
      });

      const sections = collectFromSections(schema);
      const schemaHash = document.getElementById("ev_schema_hash")?.value || "";
      const payload = await buildEncryptedPayload({
        sections,
        schemaHash,
        orderId: form?.dataset?.orderId
      });

      debugLog('submit-payload', {
        requestId: payload.request_id,
        ciphertextLength: payload.ct_b64.length,
        tagLength: payload.tag_b64.length,
        ivLength: payload.iv_b64.length,
        hash: payload.ballot_hash_hex
      });

      const ctField = document.getElementById("ev_ciphertext");
      const ivField = document.getElementById("ev_iv");
      const wkField = document.getElementById("ev_key_wrapped");
      const tagField = document.getElementById("ev_tag");
      const hashField = document.getElementById("ev_ballot_hash");
      const requestField = document.getElementById("ev_request_id");

      if (!ctField || !ivField || !wkField || !tagField || !hashField || !requestField) {
        throw new Error("Missing encrypted fields");
      }

      ctField.value = payload.ct_b64;
      ivField.value = payload.iv_b64;
      wkField.value = payload.key_wrapped_b64;
      tagField.value = payload.tag_b64;
      hashField.value = payload.ballot_hash_hex;
      requestField.value = payload.request_id;

      debugLog('submit-fields-set', {
        ciphertext: !!ctField.value,
        iv: !!ivField.value,
        wrappedKey: !!wkField.value,
        tag: !!tagField.value,
        ballotHash: hashField.value,
        requestId: requestField.value
      });

      form.submit();
    } catch (e) {
      debugLog('submit-error', { message: e?.message, stack: e?.stack });
      if (errors) {
        errors.classList.remove("hidden");
        errors.textContent = e?.message || "Submission failed.";
      }
      console.error("Ballot submit error:", e);
    }
  })();
}

// =============== Initialization ===============
function initElectionBallot() {

  const container = document.getElementById("ballot-sections");
  const schemaJSON = document.getElementById("ballot-schema")?.textContent;

  if (!container || !schemaJSON) return;

  try {
    const schema = JSON.parse(schemaJSON || "{}");
    renderBallot(schema);

    // Initialize empty review
    updateBallotReview();

    const form = document.getElementById("electronic-vote-form");
    if (form) {
      form.addEventListener("submit", () => {
        debugLog('form-submit-event', {
          orderId: form.dataset?.orderId || null,
          confirmChecked: document.getElementById('confirm-checkbox')?.checked || false
        });
      });
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        runEncryptionPipeline(form);
      });
      if (form.dataset) {
        form.dataset.ballotListener = 'attached';
      }
      debugLog('form-listener-attached', { orderId: form.dataset?.orderId || null });
    }

    const submitBtn = document.getElementById("submit-vote");
    if (submitBtn) {
      submitBtn.addEventListener("click", () => {
        debugLog('submit-button-click', {
          confirmChecked: document.getElementById('confirm-checkbox')?.checked || false,
          buttonDisabled: submitBtn.disabled
        });
      });
    }

    // Export functions for testing
    window.renderBallot = renderBallot;
    window.collectFromSections = (s = schema) => collectFromSections(s, { validate: false });
    window.updateBallotReview = updateBallotReview;
  } catch (e) {
    console.error("Failed to initialize ballot:", e);
  }
}

// Event listeners
document.addEventListener("turbo:load", initElectionBallot);
document.addEventListener("turbolinks:load", initElectionBallot);
document.addEventListener("DOMContentLoaded", initElectionBallot);

// Export for specs
window.__BallotUI__ = { renderBallot, collectFromSections, updateBallotReview };
